/**
 * Editor de dirección del perfil (MVP-02B §3).
 *
 * Toda la lógica (carga, validación, código postal, guardado) vive en
 * `domain/direcciones`. Aquí solo queda el chrome de la pantalla de perfil,
 * para que este formulario y el de checkout no puedan volver a divergir.
 */
import { useEffect, useState } from "react";
import { ArrowLeft, MapPin, Trash2 } from "lucide-react";

import {
    DireccionFormFields,
    parseErroresDireccion,
    useDireccionForm,
} from "../../../../../../domain/direcciones/index.js";
import {
    createDireccion,
    fetchDireccion,
    deleteDireccion,
    updateDireccion,
} from "../../../../../../domain/direcciones/api.js";
import {
    confirmDelete,
    showAlert,
    showErrorAlert,
} from "../../../../../../components/ui/index.js";
import { useEditor } from "../../../../../../App.jsx";

const DireccionEditor = ({ id, setEditor, setEditorID }) => {
    const { stateEditor, setStateEditor } = useEditor();
    const isNew = !id || id === "add";

    const form = useDireccionForm();
    const { reset, setErrors, validar, value } = form;

    const [isSaving, setIsSaving] = useState(false);
    const [loadState, setLoadState] = useState(isNew ? "ready" : "loading");

    // Vuelve al editor de perfil, directamente a la pestaña de Direcciones
    const goBackToDirecciones = () => {
        setEditor?.(false);
        setEditorID?.(null);
        setStateEditor({
            ...stateEditor,
            editor: true,
            editorType: "profileUser",
            editorID: null,
            profileTab: 3,
        });
    };

    useEffect(() => {
        if (isNew) {
            setLoadState("ready");
            return;
        }
        let cancelled = false;
        setLoadState("loading");
        fetchDireccion(id)
            .then((data) => {
                if (cancelled) return;
                reset(data);
                setLoadState("ready");
            })
            .catch(() => {
                if (!cancelled) setLoadState("error");
            });
        return () => {
            cancelled = true;
        };
    }, [id, isNew, reset]);

    const handleSubmit = async () => {
        if (!validar()) return;
        setIsSaving(true);
        try {
            const guardada = isNew
                ? await createDireccion(value)
                : await updateDireccion(id, value);

            const result = await showAlert({
                title: isNew ? "Dirección creada" : "Dirección actualizada",
                text: "¿Quieres volver a tus direcciones?",
                icon: "success",
                showCancelButton: true,
                confirmButtonText: "Volver a direcciones",
                cancelButtonText: "Seguir editando",
            });
            if (result.isConfirmed) {
                goBackToDirecciones();
            } else if (isNew && guardada?.id) {
                setEditorID?.(guardada.id);
                setStateEditor({ ...stateEditor, editorID: guardada.id });
            }
        } catch (error) {
            // Los errores por campo se pintan junto a su input; solo lo que no
            // encaja en un campo concreto va al aviso general.
            setErrors(parseErroresDireccion(error));
            showErrorAlert({
                text: "Revisa los campos marcados: no se pudo guardar la dirección.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        const ok = await confirmDelete({
            title: "¿Eliminar esta dirección?",
            text: "Esta acción no se puede deshacer.",
        });
        if (!ok) return;
        try {
            await deleteDireccion(id);
            goBackToDirecciones();
        } catch {
            showErrorAlert({ text: "No se pudo eliminar la dirección." });
        }
    };

    return (
        <div className="glassCard rounded-2xl p-6 max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-base-300">
                <button
                    onClick={goBackToDirecciones}
                    className="btn btn-ghost btn-sm btn-circle flex-shrink-0"
                    title="Volver a mis direcciones"
                    aria-label="Volver a mis direcciones"
                    type="button"
                >
                    <ArrowLeft size={18} />
                </button>
                <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                    <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-base-content truncate">
                        {isNew ? "Nueva dirección" : "Editar dirección"}
                    </h2>
                    <p className="text-base-content/70 text-sm truncate">
                        Escribe tu código postal y rellenamos provincia y municipio por ti
                    </p>
                </div>
            </div>

            {loadState === "loading" && (
                <div className="flex flex-col gap-4" aria-busy="true">
                    <div className="h-10 rounded-lg bg-base-300/60 animate-pulse" />
                    <div className="h-10 rounded-lg bg-base-300/60 animate-pulse" />
                    <div className="h-10 rounded-lg bg-base-300/60 animate-pulse" />
                    <span className="sr-only">Cargando la dirección…</span>
                </div>
            )}

            {loadState === "error" && (
                <div role="alert" className="flex flex-col items-start gap-3 py-6">
                    <p className="text-sm text-error">No se pudo cargar esta dirección.</p>
                    <button type="button" className="btn btn-sm" onClick={goBackToDirecciones}>
                        Volver a mis direcciones
                    </button>
                </div>
            )}

            {loadState === "ready" && (
                <>
                    <DireccionFormFields form={form} variant="profile" disabled={isSaving} />

                    <div className="pt-6 mt-6 border-t border-base-300 flex items-center justify-between gap-4">
                        {!isNew ? (
                            <button
                                onClick={handleDelete}
                                className="btn btn-outline btn-error btn-sm gap-2"
                                type="button"
                            >
                                <Trash2 size={14} />
                                Eliminar
                            </button>
                        ) : (
                            <span className="text-sm text-base-content/70">
                                Los campos con asterisco son obligatorios
                            </span>
                        )}
                        <button
                            onClick={handleSubmit}
                            disabled={isSaving}
                            className={`btn btn-primary btn-sm ${isSaving ? "loading" : ""}`}
                            type="button"
                        >
                            {isSaving ? "Guardando…" : isNew ? "Crear dirección" : "Guardar cambios"}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default DireccionEditor;
